import type { WorkflowStepFieldReference, WorkflowStepType } from 'src/gql/graphql';
import {
  type WorkflowDefinition,
  type WorkflowDefinitionEdge,
  type WorkflowDefinitionNode,
  type WorkflowInsertionPoint,
  WorkflowNodeType,
  type WorkflowStepCatalog,
  type WorkflowStepData,
} from './workflow-definition';

/**
 * Edits to a draft definition. Each one returns a new definition that keeps the shape the backend
 * accepts — one trigger first, every step reachable from it, no loops, an `if` with one edge per
 * output — so the canvas never holds a graph that only the save would reject.
 */
export type WorkflowDefinitionEdit =
  | { kind: 'updateField'; nodeId: string; fieldPath: string; value: unknown }
  | {
      kind: 'insertStep';
      at: WorkflowInsertionPoint;
      stepType: WorkflowStepType;
      nodeId: string;
    }
  | { kind: 'deleteStep'; nodeId: string; keepCondition?: boolean }
  | { kind: 'reset'; definition: WorkflowDefinition };

type PathToken = string | number;

// `waitMenu.content[0].message` → ['waitMenu', 'content', 0, 'message']
const parsePath = (path: string): PathToken[] =>
  path.split('.').flatMap((segment) =>
    segment
      .split(/[[\]]/)
      .filter(Boolean)
      .map((part, index) => (index > 0 && /^\d+$/.test(part) ? Number(part) : part)),
  );

const isContainer = (value: unknown): value is Record<PathToken, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads a value from step data by the dot path the backend uses for field errors:
 * `message`, `waitMenu.maxAttempts`, `waitMenu.content[0].message`.
 */
export function getAtPath(data: WorkflowStepData | undefined, path: string): unknown {
  return parsePath(path).reduce<unknown>(
    (value, token) => (isContainer(value) ? value[token] : undefined),
    data,
  );
}

/** A shallow copy of `container`, or a fresh array / object for a path that isn't there yet. */
const copyContainer = (
  container: unknown,
  isIndex: boolean,
): Record<PathToken, unknown> | unknown[] => {
  if (Array.isArray(container)) return [...(container as unknown[])];
  if (isContainer(container)) return { ...container };
  return isIndex ? [] : {};
};

const setIn = (container: unknown, [token, ...rest]: PathToken[], value: unknown): unknown => {
  const isIndex = typeof token === 'number';
  const copy = copyContainer(container, isIndex);

  const nextValue = rest.length
    ? setIn((copy as Record<PathToken, unknown>)[token], rest, value)
    : value;

  if (nextValue !== undefined) {
    (copy as Record<PathToken, unknown>)[token] = nextValue;
  } else if (Array.isArray(copy) && isIndex) {
    copy.splice(token, 1);
  } else {
    delete (copy as Record<PathToken, unknown>)[token];
  }

  return copy;
};

/** Returns a copy of `data` with the value at `path` replaced; `undefined` removes the key. */
export function setAtPath(
  data: WorkflowStepData | undefined,
  path: string,
  value: unknown,
): WorkflowStepData {
  return setIn(data ?? {}, parsePath(path), value) as WorkflowStepData;
}

const nextFreeId = (taken: Set<string>, base: string): string => {
  let n = 1;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
};

/** A fresh id for a new step of `stepType`, unique within the definition. */
export function createStepId(definition: WorkflowDefinition, stepType: string): string {
  return nextFreeId(new Set(definition.nodes.map(({ id }) => id)), stepType);
}

/** Hands out edge ids that no edge in `definition`, nor an earlier id from this call, has. */
const edgeIdAllocator = (definition: WorkflowDefinition) => {
  const taken = new Set(definition.edges.flatMap(({ id }) => (id ? [id] : [])));
  return () => {
    const id = nextFreeId(taken, 'edge');
    taken.add(id);
    return id;
  };
};

const conditionData = (condition: boolean | undefined) =>
  typeof condition === 'boolean' ? { data: { condition } } : {};

type EditOf<Kind extends WorkflowDefinitionEdit['kind']> = Omit<
  Extract<WorkflowDefinitionEdit, { kind: Kind }>,
  'kind'
>;

export function updateStepField(
  definition: WorkflowDefinition,
  { nodeId, fieldPath, value }: EditOf<'updateField'>,
): WorkflowDefinition {
  return {
    ...definition,
    nodes: definition.nodes.map((node) =>
      node.id === nodeId ? { ...node, data: setAtPath(node.data, fieldPath, value) } : node,
    ),
  };
}

const updateNodeData = (
  definition: WorkflowDefinition,
  nodeId: string,
  data: WorkflowStepData,
): WorkflowDefinition => ({
  ...definition,
  nodes: definition.nodes.map((node) => (node.id === nodeId ? { ...node, data } : node)),
});

/** The nodes reachable from the trigger by following edges. */
const reachableFromTrigger = (definition: WorkflowDefinition): Set<string> => {
  const trigger =
    definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger) ??
    definition.nodes[0];
  const reached = new Set<string>();
  const queue = trigger ? [trigger.id] : [];

  while (queue.length) {
    const id = queue.shift()!;
    if (reached.has(id)) continue;
    reached.add(id);
    definition.edges.forEach(({ source, target }) => {
      if (source === id) queue.push(target);
    });
  }

  return reached;
};

/** Wires each top-level reference field to the nearest providing step above `nodeId`. */
const referenceData = (
  definition: WorkflowDefinition,
  nodeId: string,
  stepType: WorkflowStepType,
): WorkflowStepData =>
  stepType.fields.reduce<WorkflowStepData>((data, field) => {
    if (!field.reference) return data;
    const source = findReferenceSource(definition, nodeId, field.reference);
    return source
      ? { ...data, [field.key]: `{{state.${source.id}.${field.reference.outputPath}}}` }
      : data;
  }, {});

/** The step id a `{{state.<stepId>.…}}` reference reads from, if it is one. */
const referencedStepId = (value: unknown) =>
  typeof value === 'string' ? /^\{\{\s*state\.([^.}\s]+)\./.exec(value)?.[1] : undefined;

/**
 * Wires every reference field — Enqueue's `conversationId`, say — that is empty or reads from a
 * step no longer in the workflow to the nearest providing step above, as a new step gets on insert;
 * with no provider above, a dangling reference is cleared. A reference to a step that is still
 * there is left as it is. Runs on load and after every added or deleted step, so what the canvas
 * shows is what gets saved. Comes back as the same object when nothing changed.
 */
export function wireReferences(
  definition: WorkflowDefinition,
  catalog: WorkflowStepCatalog,
): WorkflowDefinition {
  const ids = new Set(definition.nodes.map(({ id }) => id));
  let wiredDefinition = definition;

  for (const node of definition.nodes) {
    const stepType = catalog.byType.get(node.type);
    if (!stepType) continue;

    const wired = referenceData(wiredDefinition, node.id, stepType);
    const data: WorkflowStepData = { ...node.data };
    let changed = false;
    for (const field of stepType.fields) {
      if (!field.reference) continue;
      const current = data[field.key];
      const sourceId = referencedStepId(current);
      const stale =
        current === undefined || current === '' || (!!sourceId && !ids.has(sourceId));
      if (!stale || current === wired[field.key]) continue;

      if (wired[field.key] === undefined) delete data[field.key];
      else data[field.key] = wired[field.key];
      changed = true;
    }
    if (changed) wiredDefinition = updateNodeData(wiredDefinition, node.id, data);
  }
  return wiredDefinition;
}

/**
 * Inserts a step of `stepType` at `at`, with id `nodeId`. It starts with empty `data` — the catalog
 * carries no defaults — except that a field with a `reference` is wired to the nearest step above
 * that provides it, as `{{state.<stepId>.<outputPath>}}`.
 *
 * On a connection, the new step goes between its two ends. A new `if` keeps whatever followed on
 * its true output and leaves the false output empty; a run that answers false ends there until a
 * step is added on it.
 */
export function insertStep(
  definition: WorkflowDefinition,
  { at, stepType, nodeId }: EditOf<'insertStep'>,
): WorkflowDefinition {
  const nextEdgeId = edgeIdAllocator(definition);
  let sourceId: string;
  let edges: WorkflowDefinitionEdge[];

  if (at.kind === 'edge') {
    const split = definition.edges.find(({ id }) => id === at.edgeId);
    if (!split) return definition;

    sourceId = split.source;
    const onward: WorkflowDefinitionEdge = {
      id: nextEdgeId(),
      source: nodeId,
      target: split.target,
      ...conditionData(stepType.type === WorkflowNodeType.If ? true : undefined),
    };
    // the connection keeps its id and condition and now ends at the new step
    edges = definition.edges.flatMap((edge) =>
      edge === split ? [{ ...edge, target: nodeId }, onward] : [edge],
    );
  } else {
    sourceId = at.sourceId;
    edges = [
      ...definition.edges,
      {
        id: nextEdgeId(),
        source: at.sourceId,
        target: nodeId,
        ...conditionData(at.condition),
      },
    ];
  }

  const sourceIndex = definition.nodes.findIndex(({ id }) => id === sourceId);
  const withNode: WorkflowDefinition = {
    ...definition,
    // next to the step it follows, so the stored JSON reads in order
    nodes: [
      ...definition.nodes.slice(0, sourceIndex + 1),
      { id: nodeId, type: stepType.type, data: {} },
      ...definition.nodes.slice(sourceIndex + 1),
    ],
    edges,
  };

  return updateNodeData(withNode, nodeId, referenceData(withNode, nodeId, stepType));
}

/**
 * Deletes a step and closes the gap: the step above connects to the step below.
 *
 * Deleting an `if` keeps the output whose condition is `keepCondition` and drops the steps that
 * only the other output reached. The trigger can't be deleted; the definition comes back unchanged.
 */
export function deleteStep(
  definition: WorkflowDefinition,
  nodeId: string,
  keepCondition = true,
): WorkflowDefinition {
  const deleted = definition.nodes.find(({ id }) => id === nodeId);
  if (!deleted || deleted.type === WorkflowNodeType.Trigger) return definition;

  const outgoing = definition.edges.filter(({ source }) => source === nodeId);
  const kept =
    deleted.type === WorkflowNodeType.If
      ? outgoing.find(({ data }) => data?.condition === keepCondition)
      : outgoing[0];

  const edges = definition.edges.flatMap((edge) => {
    if (edge.source === nodeId) return [];
    if (edge.target !== nodeId) return [edge];
    // the connection into the deleted step now goes on to what followed it, keeping its condition
    return kept ? [{ ...edge, target: kept.target }] : [];
  });

  const before = reachableFromTrigger(definition);
  const joined: WorkflowDefinition = {
    ...definition,
    nodes: definition.nodes.filter(({ id }) => id !== nodeId),
    edges,
  };
  const after = reachableFromTrigger(joined);
  // only what this delete cut off goes; a step that was already unreachable is left for the save
  // to report rather than silently removed
  const dropped = new Set([...before].filter((id) => !after.has(id)));

  return {
    ...joined,
    nodes: joined.nodes.filter(({ id }) => !dropped.has(id)),
    edges: joined.edges.filter(
      ({ source, target }) => !dropped.has(source) && !dropped.has(target),
    ),
  };
}

/** How many steps deleting this `if` and keeping `keepCondition` would remove besides the `if` itself. */
export function countStepsDroppedWithIf(
  definition: WorkflowDefinition,
  ifNodeId: string,
  keepCondition: boolean,
): number {
  return (
    definition.nodes.length - deleteStep(definition, ifNodeId, keepCondition).nodes.length - 1
  );
}

/**
 * The outputs that have nothing after them, where the canvas offers an "add a step" target:
 * the end of each path, and an `if` output with no edge.
 */
export function getOpenOutputs(
  definition: WorkflowDefinition,
  catalog: WorkflowStepCatalog,
): Extract<WorkflowInsertionPoint, { kind: 'output' }>[] {
  return definition.nodes.flatMap((node) => {
    // an unknown step is read-only, and nothing can be added after it
    const stepType = catalog.byType.get(node.type);
    if (!stepType) return [];

    const outgoing = definition.edges.filter(({ source }) => source === node.id);

    return stepType.outputs.flatMap(({ condition }) => {
      if (typeof condition !== 'boolean') {
        return outgoing.length ? [] : [{ kind: 'output' as const, sourceId: node.id }];
      }

      return outgoing.some(({ data }) => data?.condition === condition)
        ? []
        : [{ kind: 'output' as const, sourceId: node.id, condition }];
    });
  });
}

/**
 * The nearest step above `nodeId` whose type the reference names — the step a field such as
 * `conversationId` reads its value from. `null` when no step on the way up from the trigger has
 * one of those types.
 */
export function findReferenceSource(
  definition: WorkflowDefinition,
  nodeId: string,
  reference: WorkflowStepFieldReference,
): WorkflowDefinitionNode | null {
  const byId = new Map(definition.nodes.map((node) => [node.id, node]));
  const visited = new Set<string>([nodeId]);
  let frontier = [nodeId];

  // breadth-first up the incoming edges, so the closest provider wins
  while (frontier.length) {
    const parents = definition.edges
      .filter(({ target }) => frontier.includes(target))
      .map(({ source }) => source)
      .filter((id) => !visited.has(id));

    for (const id of parents) {
      visited.add(id);
      const parent = byId.get(id);
      if (parent && reference.stepTypes.includes(parent.type)) return parent;
    }

    frontier = parents;
  }

  return null;
}

export function workflowDefinitionReducer(
  definition: WorkflowDefinition,
  edit: WorkflowDefinitionEdit,
): WorkflowDefinition {
  switch (edit.kind) {
    case 'updateField':
      return updateStepField(definition, edit);
    case 'insertStep':
      return insertStep(definition, edit);
    case 'deleteStep':
      return deleteStep(definition, edit.nodeId, edit.keepCondition);
    case 'reset':
      return edit.definition;
  }
}
